import {
  GmailRegistrationResponse,
  GoogleAuthRequest,
  GoogleRegisterRequest,
  GoogleRegistrationResponse,
  LoginData,
  LoginResponse,
  PhoneForgotPasswordRequest,
  PhoneLoginOtpRequest,
  PhoneLoginRequest,
  PhoneOtpResponse,
  PhoneRegisterRequest,
  PhoneResetPasswordRequest,
  PhoneSendOtpRequest,
  PhoneVerifyOtpRequest,
  RefreshTokenResponse,
  RegisterRequest,
} from "../types/auth";
import { ApiResponse } from "../types/common";

import { ApiClient } from "./apiClient";

const authService = new ApiClient<ApiResponse<any>>("/auth");

export const login = (data: LoginData): Promise<ApiResponse<LoginResponse>> => {
  return authService.custom("post", "/login", data);
};

export const register = (
  data: RegisterRequest
): Promise<ApiResponse<{ message: string }>> => {
  return authService.custom("post", "/register", data);
};

export const logout = (refreshToken: string): Promise<ApiResponse<void>> => {
  return authService.custom("post", "/logout", {
    token: refreshToken,
    reason: "User logout",
  });
};

export const refreshTokenRequest = (
  token: string
): Promise<ApiResponse<RefreshTokenResponse>> => {
  return authService.custom("post", "/refresh", { refreshToken: token });
};

export const verifyEmail = (
  token: string,
  email: string
): Promise<ApiResponse<string>> => {
  return authService.custom("get", "/verify-email", undefined, {
    params: { token, email },
  });
};

export const resendVerification = (
  email: string
): Promise<ApiResponse<string>> => {
  return authService.custom("post", "/resend-verification", { email });
};

export const googleAuthenticate = (
  data: GoogleAuthRequest
): Promise<ApiResponse<GmailRegistrationResponse>> => {
  return authService.custom("post", "/google-authenticate", data);
};

export const googleRegister = (
  data: GoogleRegisterRequest
): Promise<ApiResponse<GoogleRegistrationResponse>> => {
  return authService.custom("post", "/google-register", data);
};

export const forgotPassword = (
  email: string
): Promise<ApiResponse<{ message: string }>> => {
  return authService.custom("post", "/forgot-password", { email });
};

export const resetPassword = (data: {
  email: string;
  token: string;
  newPassword: string;
  confirmNewPassword: string;
}): Promise<ApiResponse<{ message: string; token?: string }>> => {
  return authService.custom("post", "/reset-password", data);
};

export const setPassword = (data: {
  googleIdToken?: string;
  newPassword: string;
  confirmNewPassword: string;
}): Promise<ApiResponse<{ message: string; token?: string }>> => {
  // Remove googleIdToken if undefined to avoid sending it in request
  const requestData: any = {
    newPassword: data.newPassword,
    confirmNewPassword: data.confirmNewPassword,
  };
  if (data.googleIdToken) {
    requestData.googleIdToken = data.googleIdToken;
  }
  return authService.custom("post", "/set-password", requestData);
};

export const changePassword = (data: {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}): Promise<ApiResponse<{ message: string }>> => {
  return authService.custom("put", "/change-mypassword", data);
};

// Phone Auth API
export const sendPhoneOtp = (
  data: PhoneSendOtpRequest
): Promise<ApiResponse<PhoneOtpResponse>> => {
  return authService.custom("post", "/phone/send-otp", data);
};

export const sendLoginPhoneOtp = (
  data: PhoneSendOtpRequest
): Promise<ApiResponse<PhoneOtpResponse>> => {
  return authService.custom("post", "/phone/send-login-otp", data);
};

export const verifyPhoneOtp = (
  data: PhoneVerifyOtpRequest
): Promise<ApiResponse<PhoneOtpResponse>> => {
  return authService.custom("post", "/phone/verify-otp", data);
};

export const phoneRegister = (
  data: PhoneRegisterRequest
): Promise<ApiResponse<LoginResponse>> => {
  return authService.custom("post", "/phone/register", data, {
    timeout: 30000,
  });
};

export const phoneLogin = (
  data: PhoneLoginRequest
): Promise<ApiResponse<LoginResponse>> => {
  return authService.custom("post", "/phone/login", data, { timeout: 30000 });
};

export const phoneLoginOtp = (
  data: PhoneLoginOtpRequest
): Promise<ApiResponse<LoginResponse>> => {
  return authService.custom("post", "/phone/login-otp", data, {
    timeout: 30000,
  });
};

export const phoneForgotPassword = (
  data: PhoneForgotPasswordRequest
): Promise<ApiResponse<PhoneOtpResponse>> => {
  return authService.custom("post", "/phone/forgot-password", data);
};

export const phoneResetPassword = (
  data: PhoneResetPasswordRequest
): Promise<ApiResponse<{ message: string }>> => {
  return authService.custom("post", "/phone/reset-password", data, {
    timeout: 30000,
  });
};

export default authService;
